import cn from 'classnames';
import { RotateCcwIcon, RotateCwIcon } from 'lucide-react';
import Cropper from 'react-cropper';

import 'cropperjs/dist/cropper.css';

import { Icon, UiButton, UiText } from '@shared/ui';

import cls from './AvatarTabContent.module.css';
import {
    ACCEPTED_AVATAR_IMAGE_TYPES,
    AVATAR_MAX_DIMENSION_PX,
    AVATAR_MAX_SIZE_MB,
} from '../../lib/avatarFile';
import { useAvatarUpload } from '../../model/useAvatarUpload';

export const AvatarTabContent = () => {
    const {
        t,
        cropperRef,
        fileInputRef,
        imageSrc,
        error,
        isDragActive,
        isUploading,
        openFileDialog,
        resetCrop,
        rotateLeft,
        rotateRight,
        submit,
        handleInputChange,
        handleDragEnter,
        handleDragOver,
        handleDragLeave,
        handleDrop,
    } = useAvatarUpload();

    return (
        <div className={cls.avatarContent}>
            <div className={cls.avatarRestrictions}>
                <UiText tag="span" className={cls.avatarAttention}>
                    {t('profile.page.avatar.attention')}
                </UiText>
                <UiText variant="subPlain" className={cls.avatarRestrictionsText}>
                    {t('profile.page.avatar.restrictions', {
                        dimensions: `${AVATAR_MAX_DIMENSION_PX}x${AVATAR_MAX_DIMENSION_PX}`,
                        size: AVATAR_MAX_SIZE_MB,
                    })}
                </UiText>
            </div>

            {!imageSrc ? (
                <div
                    className={cls.avatarDropzone}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    <button
                        type="button"
                        className={cn(
                            cls.avatarDropzoneButton,
                            isDragActive && cls.avatarDropzoneButtonActive
                        )}
                        onClick={openFileDialog}
                    >
                        <Icon iconName="camera" fill="currentColor" />
                        <UiText align="center" weight="bold">
                            {t('profile.page.avatar.select')}
                        </UiText>
                    </button>
                    {isDragActive && (
                        <UiText tag="span" className={cls.avatarDropzoneOverlay}>
                            {t('profile.page.avatar.dropOverlay')}
                        </UiText>
                    )}
                    <input
                        ref={fileInputRef}
                        type="file"
                        className={cls.avatarFileInput}
                        accept={ACCEPTED_AVATAR_IMAGE_TYPES.join(',')}
                        onChange={handleInputChange}
                    />
                </div>
            ) : (
                <div className={cls.avatarCropFlow}>
                    <div className={cls.avatarCropperBox}>
                        <Cropper
                            ref={cropperRef}
                            src={imageSrc}
                            aspectRatio={1}
                            viewMode={1}
                            minContainerHeight={280}
                            background={false}
                            guides
                            responsive
                            autoCropArea={1}
                            checkOrientation
                            style={{ width: '100%', height: 360 }}
                        />
                    </div>
                    <div className={cls.avatarCropActions}>
                        <UiButton
                            type="button"
                            variant="icon"
                            startIcon={<RotateCcwIcon size={16} aria-hidden="true" />}
                            aria-label={t('profile.page.avatar.rotateLeft')}
                            onClick={rotateLeft}
                            disabled={isUploading}
                        />
                        <UiButton
                            type="button"
                            variant="icon"
                            startIcon={<RotateCwIcon size={16} aria-hidden="true" />}
                            aria-label={t('profile.page.avatar.rotateRight')}
                            onClick={rotateRight}
                            disabled={isUploading}
                        />
                    </div>
                    <div className={cls.avatarSubmitActions}>
                        <UiButton
                            type="button"
                            btnText={
                                isUploading
                                    ? t('profile.page.avatar.uploading')
                                    : t('profile.page.avatar.save')
                            }
                            onClick={submit}
                            disabled={isUploading}
                        />
                        <UiButton
                            type="button"
                            btnColor="secondary"
                            btnText={t('button.cancel.text')}
                            onClick={resetCrop}
                            disabled={isUploading}
                        />
                    </div>
                </div>
            )}

            {error && (
                <UiText role="alert" className={cls.avatarError}>
                    {error}
                </UiText>
            )}
        </div>
    );
};
